<template>
  <!-- Multi-chart selection summary (points #7 + #9). One chip per active DIMENSION;
       values within a chip OR (shown with "or"), dimensions AND (shown with a standalone
       "and" between chips). Every value and every chip is individually removable, plus a
       Clear all. Flat design-token styling: no side-stripes, no tinted washes. -->
  <div class="sel-bar" role="group" aria-label="Active chart selections">
    <span v-if="selections.length" class="sel-bar__lead">Filtering by</span>

    <div
      v-for="(entry, idx) in selections"
      :key="entry.widgetId"
      class="sel-bar__group">
      <span class="sel-chip">
        <span class="sel-chip__col">{{ selectionColumnLabel(entry) }}:</span>
        <span class="sel-chip__values">
          <span
            v-for="(v, vi) in entry.values"
            :key="valueKey(v, vi)"
            class="sel-val">
            <span class="sel-val__label">{{ valueLabel(v) }}</span>
            <button
              type="button"
              class="sel-val__x"
              :aria-label="`Remove ${valueLabel(v)}`"
              @click="$emit('remove-value', { widgetId: entry.widgetId, value: v.value })">
              <v-icon x-small>mdi-close</v-icon>
            </button>
            <span v-if="vi < entry.values.length - 1" class="sel-conj sel-conj--or">or</span>
          </span>
        </span>
        <button
          type="button"
          class="sel-chip__x"
          :aria-label="`Remove ${selectionColumnLabel(entry)} selection`"
          @click="$emit('remove-chip', entry.widgetId)">
          <v-icon x-small>mdi-close</v-icon>
        </button>
      </span>
      <span v-if="idx < selections.length - 1" class="sel-conj sel-conj--and">and</span>
    </div>

    <button v-if="selections.length" type="button" class="sel-bar__clear" @click="$emit('clear-all')">Clear all</button>

    <!-- Manual "Open in 3D" — shown only when auto-open is off and the selection is 1×1. -->
    <button v-if="showOpenIn3d" type="button" class="sel-bar__open3d" @click="$emit('open-in3d')">
      <v-icon x-small left>mdi-cube-outline</v-icon>Open in 3D
    </button>

    <!-- Neutral empty-match notice (decision 10): the selection is valid, it just matches no
         data. Not an error — no warning styling, no icon. -->
    <span v-if="combinedEmpty" class="sel-bar__empty" role="status">No data matches the combined selection</span>

    <!-- Failed-dimension notice (fail-closed session): names the rejected chart. -->
    <span v-if="error" class="sel-bar__error" role="alert">
      <v-icon x-small class="sel-bar__error-glyph">mdi-alert-outline</v-icon>
      <span>{{ error.message }}</span>
    </span>

    <!-- Screen-reader-only running summary of the whole boolean expression. -->
    <span class="sel-bar__sr" aria-live="polite">{{ ariaSummary }}</span>
  </div>
</template>

<script>
/* eslint-disable */
import { selectionColumnLabel } from '@/features/Dashboard/utils/dashboardReader';
export default {
  name: 'SelectionSummaryBar',
  props: {
    // Ordered dimension entries: [{ widgetId, filterColumn, values: [{ value, display }] }].
    selections: { type: Array, default: () => [] },
    // Optional transient failure notice: { message }. Null when there is nothing to report.
    error: { type: Object, default: null },
    // True when the combined selection resolved to zero tags — a NEUTRAL empty-match notice
    // (not an error): the selection is valid, it just matches no data (decision 10).
    combinedEmpty: { type: Boolean, default: false },
    // True when the preference to auto-open the 3D viewer is OFF and the selection is exactly
    // one chart / one value — surface an explicit "Open in 3D" action instead (decision 9).
    showOpenIn3d: { type: Boolean, default: false },
  },
  computed: {
    // Full boolean expression for assistive tech, e.g.
    // "Filtering by System: A or B, and Status: Open.".
    ariaSummary() {
      if (!this.selections.length) return '';
      const parts = this.selections.map(entry => {
        const values = (entry.values || []).map(v => this.valueLabel(v)).join(' or ');
        return `${selectionColumnLabel(entry)}: ${values}`;
      });
      return `Filtering by ${parts.join(', and ')}.`;
    },
  },
  methods: {
    selectionColumnLabel,
    // Human label for a value; 'Unset' stands in for a null (empty) value so the chip never
    // renders a blank token.
    valueLabel(v) {
      if (v && v.display != null && v.display !== '') return v.display;
      if (v && v.value != null && v.value !== '') return String(v.value);
      return 'Unset';
    },
    // Stable-ish key for a value node — value is unique within a dimension, but null/blank
    // collapses to the index so Vue still gets a distinct key.
    valueKey(v, vi) {
      const raw = v && v.value;
      return raw == null || raw === '' ? `i${vi}` : String(raw);
    },
  },
};
</script>

<style scoped>
.sel-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: var(--ds-surface-elevated, #fcfdfc);
  border: 1px solid var(--ds-border-subtle, rgba(0, 0, 0, 0.10));
  border-radius: 8px;
  font-family: var(--ds-font-family-body);
  font-size: 12.5px;
}

.sel-bar__lead {
  font-weight: 600;
  color: var(--v-text-base);
}

.sel-bar__group {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

/* One dimension: flat pill, hairline border, no accent stripe. */
.sel-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 6px 0 10px;
  border: 1px solid var(--ds-border-subtle, rgba(0, 0, 0, 0.14));
  border-radius: 6px;
  background: #ffffff;
}

.sel-chip__col {
  font-weight: 600;
  color: var(--v-text-base);
}

.sel-chip__values {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

/* One value token within a dimension. */
.sel-val {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.sel-val__label {
  color: var(--v-text-base);
}

/* Per-value and per-chip remove buttons — square, quiet, hover reveals affordance. */
.sel-val__x,
.sel-chip__x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: var(--v-text-base);
  cursor: pointer;
}

.sel-val__x:hover,
.sel-chip__x:hover,
.sel-val__x:focus-visible,
.sel-chip__x:focus-visible {
  background: rgba(0, 0, 0, 0.06);
  outline: none;
}

.sel-chip__x {
  margin-left: 2px;
}

/* Boolean connectives — "or" within a chip, "and" between chips. Rendered as their own
   nodes (not chip text) so the semantics are visible and readable. */
.sel-conj {
  font-style: italic;
  color: var(--v-text-base);
  opacity: 0.75;
}

.sel-conj--or {
  margin-left: 2px;
  font-size: 11.5px;
}

.sel-conj--and {
  font-weight: 600;
  font-style: normal;
  opacity: 1;
}

.sel-bar__clear {
  height: 28px;
  padding: 0 10px;
  border: 1px solid var(--ds-border-subtle, rgba(0, 0, 0, 0.14));
  border-radius: 6px;
  background: transparent;
  color: var(--v-primary-base);
  font-family: var(--ds-font-family-body);
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
}

.sel-bar__clear:hover,
.sel-bar__clear:focus-visible {
  background: rgba(0, 0, 0, 0.04);
  border-color: rgba(0, 0, 0, 0.26);
  outline: none;
}

/* Manual "Open in 3D" — same flat control language as Clear all. */
.sel-bar__open3d {
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: 0 10px;
  border: 1px solid var(--ds-border-subtle, rgba(0, 0, 0, 0.14));
  border-radius: 6px;
  background: transparent;
  color: var(--v-primary-base);
  font-family: var(--ds-font-family-body);
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
}

.sel-bar__open3d:hover,
.sel-bar__open3d:focus-visible {
  background: rgba(0, 0, 0, 0.04);
  border-color: rgba(0, 0, 0, 0.26);
  outline: none;
}

/* Neutral empty-match notice — plain text, no warning colour or icon (it is not an error). */
.sel-bar__empty {
  color: var(--v-text-base);
  font-style: italic;
  opacity: 0.85;
}

.sel-bar__error {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--v-warning-base, #a07a00);
}

.sel-bar__error-glyph {
  color: var(--v-warning-base, #a07a00) !important;
}

/* Visually hidden, still announced. */
.sel-bar__sr {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
